import type {
  ColumnApi,
  ColumnState,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community';
import { keyBy } from 'lodash';
import { pipe } from 'ramda';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { SimpleFilter } from 'api/dashboard/widget';
import { Project } from 'api/project';
import {
  asColumnOrder,
  cleanUpColumnOrder,
  ColumnOrder,
} from 'components/organisms/grid-custom-column';
import { FilterableField } from 'components/organisms/tasks/fields';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useProjectsMetaQuery } from 'queries';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import {
  CsatRatingField,
  LastMilestoneRatingField,
  PastProjectStatusField,
  ProjectHoursField,
  ProjectPhasesField,
} from './fields';
import {
  getColDef,
  relativelySortByDefaultColumnOrder,
  DEFAULT_COLUMN_ORDER,
} from './grid/columns';
import { StyledGrid } from './grid/grid';
import {
  PROJECT_LIST_COLUMNS_STATE_LOCALSTORAGE_KEY,
  useGridOptions,
} from './grid/grid-options';
import { computeRowData } from '.';

const GridWrapper = styled.div`
  height: calc(100% - 48px);
`;

export const DashBoardProjectList: FC<{
  data: Project[];
  simpleFilter?: SimpleFilter;
  fieldNames: string[];
}> = ({ data, fieldNames }) => {
  const { fields: allFields } = useProjectFieldsAsFilterableFields();
  const excludedProjectFields = useMemo(() => [''], []);
  const acceptedProjectFields = useCallback(
    ({ fieldName }: FilterableField) =>
      !excludedProjectFields.includes(fieldName),
    [excludedProjectFields],
  );

  const [columnOrderV2] = useLocalStorage<ColumnOrder[]>(
    PROJECT_LIST_COLUMNS_STATE_LOCALSTORAGE_KEY,
    DEFAULT_COLUMN_ORDER,
  );

  const { data: metaData } = useProjectsMetaQuery();

  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [gridColumnApi, setGridColumnApi] =
    useState<ColumnApi | undefined>(undefined);
  const [defaultColumnState, setDefaultColumnState] = useState<ColumnState[]>(
    [],
  );
  const [gridInitialized, setGridInitialized] = useState(false);

  const columnFields = useMemo(
    () =>
      [
        ...allFields,
        PastProjectStatusField,
        LastMilestoneRatingField,
        CsatRatingField,
        ProjectHoursField,
        ProjectPhasesField,
      ].filter(acceptedProjectFields),
    [allFields, acceptedProjectFields],
  );
  const colRef = useRef(columnFields);

  useEffect(() => {
    if (gridInitialized) {
      return;
    }
    colRef.current = columnFields;
  }, [columnFields, gridInitialized]);
  const fieldLookupById = useMemo(
    () => keyBy(columnFields, 'fieldId'),
    [columnFields],
  );

  useEffect(() => {
    if (!gridColumnApi || defaultColumnState.length === 0) {
      return;
    }

    const newColumnOrder =
      cleanUpColumnOrder(defaultColumnState)(columnOrderV2);

    const { selectedFields, otherFields } = newColumnOrder.reduce(
      (
        acc: { selectedFields: ColumnOrder[]; otherFields: ColumnOrder[] },
        e: ColumnOrder,
      ) => {
        const isFieldIncluded = fieldNames.includes(e.colId ?? '');

        if (isFieldIncluded) {
          acc.selectedFields.push(e);
        } else {
          acc.otherFields.push(e);
        }

        return acc;
      },
      { selectedFields: [], otherFields: [] },
    );

    gridColumnApi.setColumnState([...selectedFields, ...otherFields]);
  }, [columnOrderV2, defaultColumnState, fieldNames, gridColumnApi]);

  useEffect(() => {
    if (!gridApi || !columnFields.length) {
      return;
    }
    const toColumnDefs = pipe(getColDef, relativelySortByDefaultColumnOrder);
    const columnDefs = toColumnDefs(columnFields);

    gridApi?.setColumnDefs(columnDefs);
  }, [columnFields, gridApi]);
  useEffect(() => {
    if (!gridApi || !data || !columnFields.length) {
      return;
    }
    if (gridInitialized) {
      return;
    }

    const rowData = computeRowData({
      metaData,
      data,
      fieldLookupById,
    });

    gridApi.setRowData(rowData);
    gridApi.refreshClientSideRowModel();
    if (metaData?.length) {
      setGridInitialized(true);
    }
  }, [
    columnFields.length,
    data,
    fieldLookupById,
    gridApi,
    gridInitialized,
    metaData,
  ]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
    setGridColumnApi(event.columnApi);
    setDefaultColumnState(asColumnOrder(event.columnApi.getColumnState()));
  }, []);

  useEffect(() => {
    if (gridApi) {
      gridApi.refreshHeader();
      if (gridApi.getDisplayedRowCount() < 1) {
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.hideOverlay();
      }
    }
  }, [gridApi]);

  const gridOptions = useGridOptions(colRef.current, () => {});

  return (
    <GridWrapper className="ag-theme-balham">
      <StyledGrid
        {...gridOptions}
        size="large"
        onGridReady={onGridReady}
        animateRows
        disableStaticMarkup
        suppressMovableColumns
        suppressClickEdit
      />
    </GridWrapper>
  );
};
